import { withListOverflow } from "../../v2/components/ListOverflow.jsx";
export const CostLeakageReport = withListOverflow(CostLeakageListOwner);
import React, { useEffect, useMemo, useRef, useState } from "react";
import { T } from "../../app/tokens.js";
import { ReportShell } from "./ReportShell.jsx";
import { getCostLeakageReport } from "../../data/reportsClient.js";
import { KpiTile } from "../../components/KpiTile.jsx";
import { KpiPopover, HOME_KPI_TOOLTIP_STYLE } from "../../components/KpiPopover.jsx";
import { KPI_TILE_ROW_HEIGHT } from "../plan/KpiTileRow.jsx";
import { KpiBand } from "../../components/KpiBand.jsx";
import { FxBasisCaption } from "../../components/FxBasisCaption.jsx";

const SOURCES = [
  { key: "vacated", label: "Vacated leases carrying rent" },
  { key: "underused", label: "Underused office seats" },
  { key: "unsublet", label: "Unsublet gap" },
  { key: "dormant", label: "Dormant / idle offices" },
  { key: "holdover", label: "Holdover premium" },
];
const TYPE_LABEL = { vacated: "Vacated", dormant: "Dormant", holdover: "Holdover", unsublet: "Unsublet", underused: "Under-use" };
const TABS = [
  { key: "overview", label: "Overview" },
  { key: "dead", label: "Dead Rent" },
  { key: "under", label: "Under-Use" },
  { key: "office", label: "By Office" },
];
const AMBER = T.ragAmberText;
const RED = T.ragRed;
const SOFT = T.mid;
const n = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const fmt = (v) => {
  const a = Math.abs(v);
  if (a >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `$${(v / 1e3).toFixed(1)}K`;
  return `$${Math.round(v)}`;
};
const isUnder = (r) => r.category === "under-use" || (!r.category && r.source === "underused");
const pct = (v) => (v == null || v === "" || !Number.isFinite(Number(v)) ? "—" : `${Math.round(Number(v) > 1 ? Number(v) : Number(v) * 100)}%`);
const dateTxt = (d) => { if (!d) return ""; const x = new Date(d); return Number.isNaN(x.getTime()) ? String(d) : x.toLocaleDateString("en-US", { month: "short", year: "numeric" }); };
const asOfTxt = (value) => {
  if (!value) return "Not available";
  const date = String(value).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return "Not available";
  const parsed = new Date(`${date}T00:00:00Z`);
  return Number.isNaN(parsed.getTime())
    ? "Not available"
    : parsed.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
};
const moneyValue = (value, suffix = "") => value == null || !Number.isFinite(Number(value))
  ? "Not available"
  : `${fmt(Number(value))}${suffix}`;

const th = { background: T.tableHeadBg, textAlign: "left", fontSize: T.fsXxxs, fontWeight: T.fwBold, letterSpacing: ".05em", textTransform: "uppercase", color: T.light, padding: "10px 12px", borderBottom: `1px solid ${T.border}`, whiteSpace: "nowrap" };
const td = { padding: "11px 12px", borderBottom: `1px solid ${T.lightGray}`, fontSize: T.fsSm, verticalAlign: "top", lineHeight: 1.4 };
const num = { fontVariantNumeric: "tabular-nums", fontWeight: T.fwBold, textAlign: "right", whiteSpace: "nowrap" };
const card = { background: T.card, border: `1px solid ${T.border}`, borderRadius: 12 };
const totTd = { ...td, background: T.tableHeadBg, borderBottom: 0, fontWeight: T.fwBold };
const pill = (bg, fg) => ({ display: "inline-block", fontSize: T.fsXxxs, fontWeight: T.fwBold, letterSpacing: ".04em", textTransform: "uppercase", borderRadius: 5, padding: "2px 7px", background: bg, color: fg });

function Note({ testId, role, children, action }) {
  return (
    <div data-testid={testId} role={role} style={{ ...card, padding: "28px 20px", textAlign: "center", color: T.mid, fontSize: T.fsBase }}>
      <div>{children}</div>
      {action}
    </div>
  );
}

function SiteCell({ name, sub }) {
  return (
    <td style={td}>
      <span style={{ fontWeight: T.fwBold, color: T.hdrFrom }}>{name || "Unassigned"}</span>
      {sub ? <span style={{ display: "block", fontSize: T.fsXxs, color: T.light }}>{sub}</span> : null}
    </td>
  );
}

function TableWrap({ id, label, children }) {
  return (
    <div style={{ overflowX: "auto", ...card }} data-testid={`cost-leakage-${id}-scroll`} tabIndex={0} role="region" aria-label={label}>
      <table data-testid={`cost-leakage-${id}-table`} style={{ width: "100%", minWidth: 720, borderCollapse: "collapse" }}>
        <caption style={{ position: "absolute", left: -9999 }}>{label}</caption>
        {children}
      </table>
    </div>
  );
}

function Donut({ segs, total }) {
  const r = 70, c = 2 * Math.PI * r;
  let off = 0;
  return (
    <svg data-testid="cost-leakage-donut" role="img" width="188" height="188" viewBox="0 0 188 188"
      aria-label={segs.map((s) => `${s.label} ${fmt(s.value)}`).join(", ")}>
      <g transform="rotate(-90 94 94)">
        <circle cx="94" cy="94" r={r} fill="none" stroke={T.lightGray} strokeWidth="26" />
        {total > 0 && segs.map((s) => {
          const len = (s.value / total) * c;
          const el = <circle key={s.id} data-testid={`cost-leakage-donut-${s.id}`} cx="94" cy="94" r={r} fill="none" stroke={s.color} strokeWidth="26" strokeDasharray={`${len} ${c - len}`} strokeDashoffset={-off} />;
          off += len;
          return el;
        })}
      </g>
      <text x="94" y="88" textAnchor="middle" fontSize="9" fontWeight="700" fill={T.light} letterSpacing=".06em">IDENTIFIED</text>
      <text x="94" y="112" textAnchor="middle" fontSize="28" fontWeight="700" fill={T.dark}>{fmt(total)}</text>
      <text x="94" y="128" textAnchor="middle" fontSize="11" fill={T.mid}>per year</text>
    </svg>
  );
}

function SummaryCard({ id, name, tag, tagStyle, value, valueColor, sub, rows, cta, onGo }) {
  return (
    <button type="button" data-testid={`cost-leakage-card-${id}`} onClick={onGo}
      style={{ ...card, flex: "1 1 250px", padding: "15px 16px", display: "flex", flexDirection: "column", textAlign: "left", font: "inherit", cursor: "pointer" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: T.fsBase5, fontWeight: T.fwBold, color: T.hdrFrom }}>{name}</span>
        <span data-testid={`cost-leakage-card-${id}-tag`} style={tagStyle}>{tag}</span>
      </div>
      <div style={{ fontSize: T.fsXl5, fontWeight: T.fwDisplay, color: valueColor, margin: "5px 0 2px" }}>{value}</div>
      <div style={{ fontSize: T.fsXs, color: T.mid, marginBottom: 10 }}>{sub}</div>
      <div style={{ flex: 1 }}>
        {rows.length === 0 ? <div style={{ fontSize: T.fsXs, color: T.light, borderTop: `1px solid ${T.lightGray}`, padding: "6px 0" }}>No contributors</div> : rows.map((r, i) => (
          <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: T.fsXs, padding: "4px 0", borderTop: `1px solid ${T.lightGray}` }}>
            <span style={{ color: T.mid }}>{r.name}</span>
            <span style={{ fontWeight: T.fwBold, fontVariantNumeric: "tabular-nums" }}>{r.value}</span>
          </div>
        ))}
      </div>
      <span data-testid={`cost-leakage-card-${id}-link`}
        style={{ alignSelf: "flex-end", marginTop: 10, fontSize: T.fsXs, fontWeight: T.fwBold, color: T.blue }}>
        {cta} ›
      </span>
    </button>
  );
}

function DeadRentRollOff({ data }) {
  const rows = Array.isArray(data?.annualByYear) ? data.annualByYear : [];
  const max = Math.max(...rows.map((row) => Number(row.amount) || 0), 1);
  const amount = data?.undatedDeadRentAnnual;
  return (
    <section data-testid="cost-leakage-rolloff" style={{ ...card, padding: "18px 24px", marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, flexWrap: "wrap", marginBottom: 4 }}>
        <strong style={{ fontSize: T.fsMd5, color: T.hdrFrom }}>Dead rent roll-off</strong>
        <span style={{ fontSize: T.fsXs, color: T.mid }}>
          {data?.remainingExposure == null
            ? "Remaining exposure: Not available"
            : `${fmt(data.remainingExposure)} committed in total`}
          {data?.latestRollOffYear != null ? ` · fully rolled off by ${data.latestRollOffYear}` : ""}
        </span>
      </div>
      <div style={{ display: "flex", alignItems: "stretch", gap: 16, flexWrap: "wrap" }}>
        <div data-testid="cost-leakage-rolloff-chart" role="img"
          aria-label={`Annual dead rent by year: ${rows.map((row) => `${row.year} ${fmt(Number(row.amount) || 0)}`).join(", ")}`}
          style={{ display: "flex", alignItems: "flex-end", gap: 12, height: 162, padding: "14px 4px 2px", overflowX: "auto", flex: "1 1 520px", borderBottom: `1px solid ${T.border}` }}>
          {rows.length ? rows.map((row) => {
            const value = Number(row.amount) || 0;
            const height = value > 0 ? Math.max(3, Math.round((value / max) * 100)) : 2;
            return (
              <div key={row.year} data-testid={`cost-leakage-rolloff-year-${row.year}`}
                aria-label={`${row.year}: ${fmt(value)}`}
                title={`${row.year}: ${fmt(value)}`}
                style={{ flex: "1 0 54px", minWidth: 54, height: 142, display: "flex", flexDirection: "column", justifyContent: "flex-end", alignItems: "center" }}>
                <span style={{ fontSize: T.fsXxs5, color: T.hdrFrom, fontWeight: T.fwBold, marginBottom: 5, whiteSpace: "nowrap" }}>{fmt(value)}</span>
                <div aria-hidden="true" style={{ width: "74%", maxWidth: 70, height, background: value > 0 ? `linear-gradient(180deg, ${T.ragRed}, ${T.ragRed})` : T.lightGray, borderRadius: "6px 6px 0 0" }} />
                <span style={{ color: T.mid, fontSize: T.fsXs, fontWeight: T.fwBold, marginTop: 6 }}>{row.year}</span>
              </div>
            );
          }) : <div data-testid="cost-leakage-rolloff-unavailable" style={{ color: T.mid, fontSize: T.fsSm, margin: "auto" }}>Roll-off is not available because no dated dead-rent records are available.</div>}
        </div>
        <aside data-testid="cost-leakage-rolloff-undated" style={{ flex: "0 1 205px", alignSelf: "center", border: `1px solid ${T.border}`, borderRadius: 10, padding: "12px 14px", background: T.tableHeadBg }}>
          <div style={{ color: T.mid, fontSize: T.fsXxs5, fontWeight: T.fwBold, textTransform: "uppercase", letterSpacing: ".04em" }}>End date not recorded</div>
          <div style={{ color: T.dark, fontSize: T.fsLg, fontWeight: T.fwBold, marginTop: 5 }}>{moneyValue(amount, "/yr")}</div>
          <div style={{ color: T.mid, fontSize: T.fsXs, marginTop: 3 }}>{Number(data?.undatedDeadRentCount) || 0} dead-rent {Number(data?.undatedDeadRentCount) === 1 ? "record" : "records"} excluded from the year bars</div>
        </aside>
      </div>
      <div data-testid="cost-leakage-rolloff-note" style={{ fontSize: T.fsXs, color: T.mid, lineHeight: 1.5, marginTop: 14, background: T.tableHeadBg, borderRadius: 8, padding: "9px 12px" }}>
        Each year’s bar is the dated dead rent still carried that year. Recording a sublease or break moves rent into <b>Recoverable</b> and shortens the roll-off.
      </div>
    </section>
  );
}

function CostLeakageListOwner({
  report,
  loading = false,
  error = null,
  onBack,
  prevReport = null,
  nextReport = null,
  onNavigateReport,
}) {
  const [view, setView] = useState("overview");
  const [loadedReport, setLoadedReport] = useState(null);
  const [requestLoading, setRequestLoading] = useState(report === undefined);
  const [requestError, setRequestError] = useState(null);

  useEffect(() => {
    if (report !== undefined) return undefined;
    let alive = true;
    setRequestLoading(true);
    getCostLeakageReport()
      .then((result) => {
        if (alive) setLoadedReport(result);
      })
      .catch((cause) => {
        if (alive) setRequestError(cause);
      })
      .finally(() => {
        if (alive) setRequestLoading(false);
      });
    return () => { alive = false; };
  }, [report]);

  const effectiveReport = report === undefined ? loadedReport : report;
  const effectiveLoading = loading || requestLoading;
  const effectiveError = error || requestError;
  const records = useMemo(() => (Array.isArray(effectiveReport?.records) ? effectiveReport.records : []), [effectiveReport]);
  const states = effectiveReport?.sourceStates || {};

  const agg = useMemo(() => {
    const dead = [];
    const under = [];
    const offices = new Map();
    let rec = 0, committed = 0;
    records.forEach((r) => {
      const u = isUnder(r);
      const cost = n(r.annualCost);
      const key = r.locationId ?? r.office ?? "unassigned";
      const o = offices.get(key) || { name: r.office || "Unassigned", sub: r.market, dead: 0, under: 0, srcs: new Set() };
      o.srcs.add(TYPE_LABEL[r.source] || r.source || "—");
      if (u) { under.push(r); o.under += cost; }
      else {
        dead.push(r); o.dead += cost;
        if (r.recoveryStatus === "recoverable") rec += cost;
        else committed += cost;
      }
      offices.set(key, o);
    });
    const sum = (a) => a.reduce((x, r) => x + n(r.annualCost), 0);
    const byCost = (a, b) => n(b.annualCost) - n(a.annualCost);
    dead.sort(byCost); under.sort(byCost);
    const offList = [...offices.values()].sort((a, b) => (b.dead + b.under) - (a.dead + a.under));
    const contributors = (rows) => {
      const grouped = new Map();
      rows.forEach((r) => {
        const key = r.locationId ?? r.office ?? "unassigned";
        const current = grouped.get(key) || { key, name: r.office || "Unassigned", value: 0, rows: [] };
        current.value += n(r.annualCost);
        current.rows.push(r);
        grouped.set(key, current);
      });
      return [...grouped.values()].sort((a, b) => b.value - a.value);
    };
    const underOffices = contributors(under).map(({ rows, ...office }) => {
      const representative = rows[0] || {};
      const events = rows
        .filter((r) => r.nextLeaseEvent && r.nextLeaseEventDate)
        .sort((a, b) => String(a.nextLeaseEventDate).localeCompare(String(b.nextLeaseEventDate)));
      return {
        ...office,
        sub: representative.market,
        emptySeatShare: representative.emptySeatShare,
        nextLeaseEvent: events[0]?.nextLeaseEvent || null,
        nextLeaseEventDate: events[0]?.nextLeaseEventDate || null,
      };
    });
    const deadSites = new Set(dead.map((r) => r.locationId ?? r.office));
    const underSites = new Set(under.map((r) => r.locationId ?? r.office));
    return {
      dead,
      under,
      offList,
      deadContributors: contributors(dead),
      underOffices,
      rec,
      committed,
      deadTotal: sum(dead),
      underTotal: sum(under),
      deadSites: deadSites.size,
      underSites: underSites.size,
      sites: offList.length,
    };
  }, [records]);
  const total = agg.deadTotal + agg.underTotal;
  const rollOff = effectiveReport?.rollOff || null;
  const refreshDates = effectiveReport?.sourceRefreshDates || {};
  const periodFor = (sources) => {
    const dates = sources.map((source) => ({ source, value: asOfTxt(refreshDates[source]) }));
    return dates.some(({ value }) => value === "Not available")
      ? "Not available"
      : dates.map(({ source, value }) => `${source === "spacebase" ? "Spacebase" : "Workday"}: ${value}`).join(" · ");
  };
  const coverageDetails = (keys) => coverageSources
    .filter(({ key }) => keys.includes(key))
    .map(({ key, label }) => {
      const state = states[key];
      const status = state.status === "tracked" ? "$0 / None"
        : state.status === "partial" ? "Partially tracked"
          : state.status === "not-tracked" ? "Not tracked yet"
            : "Not computable";
      return { label, value: `${status}${state.message ? ` — ${state.message}` : ""}` };
    });
  const unknownSources = SOURCES.filter(({ key }) =>
    ["partial", "not-tracked", "not-computable"].includes(states[key]?.status));
  const coverageSources = SOURCES.filter(({ key }) => {
    const state = states[key];
    return state && (
      ["partial", "not-tracked", "not-computable"].includes(state.status)
      || (state.status === "tracked" && Number(state.count) === 0)
    );
  });
  const unit = <span style={{ fontSize: T.fsSm, color: T.light }}>/yr</span>;
  const tabRefs = useRef({});
  const onTabKey = (e, i) => {
    let ni = null;
    if (e.key === "ArrowRight") ni = (i + 1) % TABS.length;
    else if (e.key === "ArrowLeft") ni = (i + TABS.length - 1) % TABS.length;
    else if (e.key === "Home") ni = 0;
    else if (e.key === "End") ni = TABS.length - 1;
    if (ni == null) return;
    e.preventDefault();
    setView(TABS[ni].key);
    tabRefs.current[TABS[ni].key]?.focus();
  };
  const emptyPanel = (id, text) => <Note testId={`cost-leakage-${id}-empty`} role="status">{text}</Note>;

  let body;
  if (effectiveLoading) {
    body = (
      <div data-testid="cost-leakage-loading" role="status" aria-live="polite" aria-label="Loading cost leakage">
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
          {[0, 1, 2, 3, 4, 5, 6].map((i) => <div key={i} style={{ ...card, flex: "1 1 160px", height: 84, background: T.lightGray }} />)}
        </div>
        <div style={{ ...card, height: 90, marginBottom: 16, background: T.lightGray }} />
        <div style={{ ...card, height: 220, background: T.lightGray }} />
      </div>
    );
  } else if (effectiveError) {
    body = (
      <Note testId="cost-leakage-error" role="alert">
        <span style={{ color: T.red, fontWeight: T.fwBold }}>Cost leakage could not be loaded.</span>{" "}
        {typeof effectiveError === "string" ? effectiveError : effectiveError?.message || ""}
      </Note>
    );
  } else if (effectiveReport?.financialsVisible === false) {
    body = <Note testId="cost-leakage-restricted" role="status">{effectiveReport.message}</Note>;
  } else {
    const segs = [
      { id: "recoverable", label: "Recoverable dead rent", value: agg.rec, color: T.ragAmber },
      { id: "committed", label: "Committed to term (no recorded exit yet)", value: agg.committed, color: RED },
      { id: "underuse", label: "Under-use (modeled, not cash)", value: agg.underTotal, color: T.utilizationUnused },
    ];
    const top3 = (a) => a.slice(0, 3).map((r) => ({ name: r.name || "Unassigned", value: fmt(r.value) }));
    const dTag = pill(T.ragRedBg, RED);
    const makePopover = ({ description, source, sourceDates, coverage = [] }) => (
      <KpiPopover period={periodFor(sourceDates)} source={source}
        details={coverageDetails(coverage)}>
        {description}
      </KpiPopover>
    );
    const kpis = [
      {
        id: "total",
        label: "Total leakage",
        value: moneyValue(total),
        unit: "/yr",
        color: RED,
        source: "Spacebase + Workday (derived)",
        sourceDates: ["spacebase", "workday"],
        coverage: ["vacated", "underused", "unsublet", "dormant", "holdover"],
        description: "All identified leakage: hard dead rent plus modeled under-use. The two are shown separately and never summed into a single hard figure.",
      },
      {
        id: "dead",
        label: "Dead rent",
        value: moneyValue(agg.deadTotal),
        unit: "/yr",
        color: RED,
        source: "Spacebase",
        sourceDates: ["spacebase"],
        coverage: ["vacated", "dormant", "holdover"],
        description: "Rent still paid on space with no productive use - vacated leases, holdover, dormant offices. Hard money you're billed for.",
      },
      {
        id: "recoverable",
        label: "Recoverable now",
        value: moneyValue(agg.rec),
        unit: "/yr",
        color: AMBER,
        source: "Spacebase",
        sourceDates: ["spacebase"],
        coverage: ["vacated", "holdover"],
        description: "Dead rent with a documented way out - an active sublease or a break option. Rises as sublease and break terms are recorded.",
      },
      {
        id: "committed",
        label: "Committed to term",
        value: moneyValue(agg.committed),
        unit: "/yr",
        color: RED,
        source: "Spacebase",
        sourceDates: ["spacebase"],
        coverage: ["vacated", "dormant", "holdover"],
        description: "Dead rent with no recorded exit - runs to lease end. Not necessarily unrecoverable: see the roll-off, and record any sublease or break to move it to Recoverable.",
      },
      {
        id: "underuse",
        label: "Under-use",
        value: moneyValue(agg.underTotal),
        unit: "/yr",
        color: SOFT,
        source: "Workday + Spacebase (derived)",
        sourceDates: ["workday", "spacebase"],
        coverage: ["underused"],
        description: "Modeled rent on empty desks in active offices (rent x empty-seat share). An estimate, not a bill - never counted in dead rent.",
      },
      {
        id: "remaining",
        label: "Remaining exposure",
        value: moneyValue(rollOff?.remainingExposure),
        color: T.hdrFrom,
        source: "Spacebase (derived)",
        sourceDates: ["spacebase"],
        description: "Total dead rent still committed across all future years, until each lease rolls off. The full cash at stake, not just this year.",
      },
      {
        id: "rolloff",
        label: "Rolls off by",
        value: rollOff?.latestRollOffYear == null ? "Not available" : String(rollOff.latestRollOffYear),
        color: T.hdrFrom,
        source: "Spacebase",
        sourceDates: ["spacebase"],
        description: "Year the last committed dead-rent lease ends, if nothing is sublet or exited sooner. After this, dead rent is $0.",
      },
    ];
    body = (
      <>
        <div data-testid="cost-leakage-kpis" style={{ marginBottom: 16 }}>
          <KpiBand testId="cost-leakage-kpi-row" ariaLabel="Report metrics">
            {kpis.map((kpi, tileIndex) => (
              <KpiTile key={kpi.id}
                testId={`cost-leakage-kpi-${kpi.id}`}
                label={kpi.label}
                value={kpi.value}
                color={kpi.color}
                labelStyle={kpi.id === "remaining" ? { fontSize: T.fsXxxs, letterSpacing: ".01em" } : undefined}
                valueStyle={kpi.value === "Not available" ? { fontSize: T.fsSm5 } : undefined}
                secondary={kpi.value === "Not available" ? undefined : kpi.unit}
                height={KPI_TILE_ROW_HEIGHT}
                tileIndex={tileIndex}
                tooltip={makePopover({ description: kpi.description, source: kpi.source, sourceDates: kpi.sourceDates, coverage: kpi.coverage })}
                tooltipStyle={HOME_KPI_TOOLTIP_STYLE}
                tooltipPlacement="viewport-clamped"
              />
            ))}
          </KpiBand>
        </div>

        <div role="tablist" aria-label="Cost leakage views" data-testid="cost-leakage-tabs" style={{ display: "flex", gap: 26, flexWrap: "wrap", borderBottom: `1px solid ${T.border}`, marginBottom: 18 }}>
          {TABS.map((t, i) => {
            const on = t.key === view;
            return (
              <button key={t.key} ref={(el) => { tabRefs.current[t.key] = el; }} type="button" role="tab" id={`cost-leakage-tab-${t.key}`}
                aria-selected={on} aria-controls={`cost-leakage-panel-${t.key}`} tabIndex={on ? 0 : -1}
                data-testid={`cost-leakage-tab-${t.key}`} onClick={() => setView(t.key)} onKeyDown={(e) => onTabKey(e, i)}
                style={{ background: "none", border: 0, borderBottom: `2.5px solid ${on ? T.accent : "transparent"}`, marginBottom: -1, padding: "0 2px 11px", cursor: "pointer", font: "inherit", fontSize: T.fsMd5, fontWeight: T.fwBold, color: on ? T.accent : T.mid }}>
                {t.label}
              </button>
            );
          })}
        </div>

        <div role="tabpanel" id={`cost-leakage-panel-${view}`} aria-labelledby={`cost-leakage-tab-${view}`} data-testid={`cost-leakage-panel-${view}`}>
          {view === "overview" && (
            <>
              {records.length === 0 && (
                <Note testId="cost-leakage-empty" role="status">
                  {unknownSources.length === 0
                    ? "No cost leakage detected for the current portfolio."
                    : "No cost leakage has been quantified yet; some source data is unavailable."}
                </Note>
              )}
              <div data-testid="cost-leakage-overview-center" style={{ ...card, padding: "22px 26px", marginBottom: 14, display: "flex", alignItems: "center", gap: 34, flexWrap: "wrap" }}>
                <Donut segs={segs} total={total} />
                <div style={{ flex: 1, minWidth: 240 }} data-testid="cost-leakage-legend">
                  <div style={{ fontSize: T.fsBase, fontWeight: T.fwBold, color: T.hdrFrom, marginBottom: 10 }}>Where the {fmt(total)}/yr goes</div>
                  {segs.map((s) => (
                    <div key={s.id} data-testid={`cost-leakage-legend-${s.id}`} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: `1px solid ${T.lightGray}` }}>
                      <span aria-hidden="true" style={{ width: 11, height: 11, borderRadius: 3, background: s.color }} />
                      <span style={{ flex: 1, fontSize: T.fsSm, color: T.mid, fontWeight: T.fwSemibold }}>{s.label}</span>
                      <span style={{ fontSize: T.fsBase, fontWeight: T.fwBold, fontVariantNumeric: "tabular-nums", color: s.id === "underuse" ? SOFT : s.id === "committed" ? RED : AMBER }}>{fmt(s.value)}</span>
                    </div>
                  ))}
                  <div data-testid="cost-leakage-recoverable-callout" style={{ marginTop: 14, background: T.ragAmberBg, border: `1px solid ${T.ragAmberBorder}`, borderRadius: 10, padding: "10px 14px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontSize: T.fsXs, fontWeight: T.fwBold, letterSpacing: ".04em", textTransform: "uppercase", color: AMBER }}>Act on this first</span>
                    <span style={{ fontSize: T.fsLg7, fontWeight: T.fwDisplay, color: AMBER }}>{fmt(agg.rec)} recoverable</span>
                  </div>
                </div>
              </div>
              <DeadRentRollOff data={rollOff} />
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <SummaryCard id="dead" name="Dead Rent" tag="Hard money" tagStyle={dTag} value={<>{fmt(agg.deadTotal)}{unit}</>} valueColor={RED}
                  sub={`Rent on space with no productive use · ${agg.deadSites} sites`} rows={top3(agg.deadContributors)}
                  cta="View dead rent" onGo={() => setView("dead")} />
                <SummaryCard id="under" name="Under-Use" tag="Modeled" tagStyle={pill(T.lightGray, T.mid)} value={<>{fmt(agg.underTotal)}{unit}</>} valueColor={SOFT}
                  sub={`Active offices, desks below seats · ${agg.underSites} sites`} rows={top3(agg.underOffices)}
                  cta="View under-use" onGo={() => setView("under")} />
                <SummaryCard id="office" name="By Office" tag="Combined" tagStyle={pill(T.blueSoft, T.blue)} value={<>{agg.sites}<span style={{ fontSize: T.fsSm, color: T.light }}> sites</span></>} valueColor={T.hdrFrom}
                  sub="Everything rolled up per location" rows={agg.offList.slice(0, 3).map((o) => ({ name: o.name, value: fmt(o.dead + o.under) }))}
                  cta="View by office" onGo={() => setView("office")} />
              </div>
            </>
          )}

          {view === "dead" && (agg.dead.length === 0 ? emptyPanel("dead", "No dead rent recorded for the current portfolio.") : (
            <TableWrap id="dead" label="Dead rent by site">
              <thead><tr>
                <th scope="col" style={th}>Site</th><th scope="col" style={th}>Type</th>
                <th scope="col" style={{ ...th, textAlign: "right" }}>Annual cost</th>
                <th scope="col" style={th}>Status &amp; why</th><th scope="col" style={th}>Next action</th>
              </tr></thead>
              <tbody>
                {agg.dead.map((r, i) => {
                  const rc = r.recoveryStatus === "recoverable";
                  const hasKnownFutureEndDate = r.rollOffEndDate === undefined
                    ? Boolean(r.endDate)
                    : Boolean(r.rollOffEndDate);
                  const relevantEndDate = r.rollOffEndDate === undefined ? r.endDate : r.rollOffEndDate;
                  const recoveryWhy = r.recoveryReason || (relevantEndDate
                    ? `Runs to term ${dateTxt(relevantEndDate)}`
                    : "No sublease or qualifying break evidence; no recorded exit yet");
                  const why = hasKnownFutureEndDate
                    ? recoveryWhy
                    : `${recoveryWhy} · End date not recorded`;
                  return (
                    <tr key={r.recordId ?? r.leaseId ?? i} data-testid={`cost-leakage-dead-row-${i}`}>
                      <SiteCell name={r.office} sub={r.market} />
                      <td style={td}><span style={pill(T.lightGray, T.mid)}>{TYPE_LABEL[r.source] || r.source || "—"}</span></td>
                      <td style={{ ...td, ...num, color: RED }}>{fmt(n(r.annualCost))}</td>
                      <td style={td}>
                        <span data-testid={`cost-leakage-dead-status-${i}`} style={rc ? pill(T.ragAmberBg, AMBER) : pill(T.ragRedBg, RED)}>{rc ? "Recoverable" : "Committed to term"}</span>
                        {why ? <div style={{ fontSize: T.fsXxs5, color: T.mid, marginTop: 3 }}>{why}</div> : null}
                      </td>
                      <td style={td}>{r.recoveryAction || (rc ? "Act on documented recovery evidence" : "Confirm lease terms and market for sublet")}</td>
                    </tr>
                  );
                })}
                <tr data-testid="cost-leakage-dead-total-row">
                  <td style={totTd}>Total</td><td style={totTd} />
                  <td style={{ ...totTd, ...num }}>{fmt(agg.deadTotal)}</td><td style={totTd} /><td style={totTd} />
                </tr>
              </tbody>
            </TableWrap>
          ))}

          {view === "under" && (
            <>
              <div data-testid="cost-leakage-modeled-notice" role="note" style={{ background: T.tableHeadBg, border: `1px dashed ${T.borderDark}`, borderRadius: 12, padding: "13px 16px", marginBottom: 14 }}>
                <span style={pill(T.border, T.mid)}>Modeled estimate — not cash you’re billed</span>
                <div style={{ fontSize: T.fsSm5, color: T.mid, marginTop: 8, lineHeight: 1.5 }}>
                  Rent allocated to empty desks in <b>active</b> offices (rent × empty-seat share). A right-sizing signal for the next lease event — kept out of the dead-rent total and never counted as recoverable.
                </div>
              </div>
              {agg.underOffices.length === 0 ? emptyPanel("under", states.underused?.status && states.underused.status !== "tracked"
                ? `Under-use is unavailable: ${states.underused.message || "source data not tracked yet"}.`
                : "No modeled under-use for the current portfolio.") : (
                <TableWrap id="under" label="Modeled under-use by office">
                  <thead><tr>
                    <th scope="col" style={th}>Office</th>
                    <th scope="col" style={{ ...th, textAlign: "right" }}>Empty share</th>
                    <th scope="col" style={{ ...th, textAlign: "right" }}>Modeled rent</th>
                    <th scope="col" style={th}>Next lease event</th>
                  </tr></thead>
                  <tbody>
                    {agg.underOffices.map((r, i) => (
                      <tr key={r.key ?? i} data-testid={`cost-leakage-under-row-${i}`}>
                        <SiteCell name={r.name} sub={r.sub} />
                        <td style={{ ...td, ...num }}>{pct(r.emptySeatShare)}</td>
                        <td style={{ ...td, ...num, color: SOFT }}>{fmt(n(r.value))}</td>
                        <td style={{ ...td, color: T.mid }}>{r.nextLeaseEvent ? `${r.nextLeaseEvent}${r.nextLeaseEventDate ? ` · ${dateTxt(r.nextLeaseEventDate)}` : ""}` : "—"}</td>
                      </tr>
                    ))}
                    <tr data-testid="cost-leakage-under-total-row">
                      <td style={totTd}>Total ({agg.underOffices.length} {agg.underOffices.length === 1 ? "office" : "offices"})</td><td style={totTd} />
                      <td style={{ ...totTd, ...num, color: SOFT }}>{fmt(agg.underTotal)}</td><td style={totTd} />
                    </tr>
                  </tbody>
                </TableWrap>
              )}
            </>
          )}

          {view === "office" && (agg.offList.length === 0 ? emptyPanel("office", "No contributing offices for the current portfolio.") : (
            <>
              <TableWrap id="office" label="Dead rent and under-use by office">
                <thead><tr>
                  <th scope="col" style={th}>Office</th>
                  <th scope="col" style={{ ...th, textAlign: "right" }}>Dead rent</th>
                  <th scope="col" style={{ ...th, textAlign: "right" }}>Under-use</th>
                  <th scope="col" style={{ ...th, textAlign: "right" }}>Total</th>
                  <th scope="col" style={th}>Contributing</th>
                </tr></thead>
                <tbody>
                  {agg.offList.map((o, i) => (
                    <tr key={i} data-testid={`cost-leakage-office-row-${i}`}>
                      <SiteCell name={o.name} sub={o.sub} />
                      <td style={{ ...td, ...num, color: o.dead ? RED : T.light }}>{o.dead ? fmt(o.dead) : "—"}</td>
                      <td style={{ ...td, ...num, color: o.under ? SOFT : T.light }}>{o.under ? fmt(o.under) : "—"}</td>
                      <td style={{ ...td, ...num }}>{fmt(o.dead + o.under)}</td>
                      <td style={{ ...td, fontSize: T.fsXs, color: T.mid }}>{[...o.srcs].join(" · ")}</td>
                    </tr>
                  ))}
                  <tr data-testid="cost-leakage-office-total-row">
                    <td style={totTd}>Total</td>
                    <td style={{ ...totTd, ...num, color: RED }}>{fmt(agg.deadTotal)}</td>
                    <td style={{ ...totTd, ...num, color: SOFT }}>{fmt(agg.underTotal)}</td>
                    <td style={{ ...totTd, ...num }}>{fmt(total)}</td><td style={totTd} />
                  </tr>
                </tbody>
              </TableWrap>
              <div style={{ fontSize: T.fsXxs5, color: T.light, marginTop: 8 }}>Dead rent and under-use stay in separate columns — combined per site, never conflated.</div>
            </>
          ))}
        </div>
        {TABS.filter((t) => t.key !== view).map((t) => (
          <div key={t.key} role="tabpanel" id={`cost-leakage-panel-${t.key}`}
            aria-labelledby={`cost-leakage-tab-${t.key}`} data-testid={`cost-leakage-panel-${t.key}`} hidden />
        ))}
      </>
    );

  }

  return (
    <ReportShell
      recordNavigation
      onBack={onBack}
      backTestId="cost-leakage-back"
      prevReport={prevReport}
      nextReport={nextReport}
      onNavigateReport={onNavigateReport}
    >
      <div
        data-testid="cost-leakage-content"
        style={{ maxWidth: 1400, padding: "0 32px 48px", margin: "0 auto", width: "100%", boxSizing: "border-box" }}
      >
        {effectiveReport?.financialsVisible !== false && (
          <div data-testid="cost-leakage-fx-basis-caption-row" style={{ marginBottom: 10 }}>
            <FxBasisCaption testId="cost-leakage-fx-basis-caption" prefix="USD figures use" />
          </div>
        )}
        {body}
      </div>
    </ReportShell>
  );
}
